import { Col, Form, Row, Tooltip } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ReactComponent as ProductAddIcon } from '../../../../../../../assets/icons/product-add.svg';
import { getStyleVariableValue } from '../../../../../../../utilz';
import { routeFileSelector } from '../../../../../../../utilz/i18n';
import IconButton from '../../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../../components/Common/SvgConverter';
import FormInput from '../../../../../../components/Inputs/FormInput';
import FormTextArea from '../../../../../../components/Inputs/FormTextArea';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import Paper from '../../../../../../components/Paper/Paper';
import ProductSelector from '../../../../../../components/Selectors/AsynchSearch/ProductSelector';
import ClaimsDispositionMethodSelector from '../../../../../../components/Selectors/ClaimsDispositionMethodSelector';
import ClaimsMitigatedTypeSelector from '../../../../../../components/Selectors/ClaimsMitigatedTypeSelector';
import ClaimsPercentageValueSelector from '../../../../../../components/Selectors/ClaimsPercentageValueSelector';
import ClaimsRegularReleaseSelector from '../../../../../../components/Selectors/ClaimsRegularReleaseSelector';
import ProductUnitUomSelector from '../../../../../../components/Selectors/ProductUnitUomSelector';
import SaveCancelButtons from '../../../../../Shipment/components/SaveCancelButtons';

const PreClaimsItemCard = ({
  editMode = false,
  isLastItem = false,
  shadowVisible = true,
  index = 0,
  itemDetails,
  setEditMode,
  handleCancel,
  handleSubmit,
  handleDelete,
  handleNewProductModal,
  onFieldsChange,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [form] = Form.useForm();

  useEffect(() => {
    if (itemDetails) {
      form.setFieldsValue(itemDetails);
    }
  }, [itemDetails]);

  const handleProductCodeChange = (value) => {
    if (!value['1']) {
      form.setFieldsValue({
        itemDesc: value?.productDesc,
        itemUnitWeight: value.productWeight,
        itemUnitMeasure: value.productUnitMeasure,
        fullRelFlag: value.fullRelFlag,
        claimantValue: value.claimantValue,
      });
    }
  };

  return (
    <Paper className={`border-muted ${!isLastItem ? 'mb-16' : ''} ${shadowVisible ? '' : 'shadow-none'}`}>
      <Form
        onFinish={(data) => handleSubmit(data, index)}
        initialValues={itemDetails}
        form={form}
        onFieldsChange={onFieldsChange}
      >
        <div className="border-bottom-muted pt-8 pb-10 px-16">
          <Row justify="space-between align-items-center">
            <Col sm={6}>
              <Row align="middle" gutter={15}>
                <Col className="flex-1">
                  <Row align="middle" gutter={15}>
                    <Col className="flex-1">
                      <ProductSelector
                        tabIndex={(index + 1) * 100 + 1}
                        name="itemCode"
                        label="Item Code"
                        placeholder="Select Item Code"
                        singleLineMode
                        margin="m-0"
                        editMode={editMode}
                        onChange={handleProductCodeChange}
                        colon
                        form={form}
                        rules={[
                          {
                            required: true,
                            message: t('translation:default.fields.productDesc.message'),
                          },
                        ]}
                      />
                    </Col>
                    {editMode && (
                      <Col>
                        <IconButton onClick={() => handleNewProductModal(index)}>
                          <Tooltip
                            color={getStyleVariableValue('--label')}
                            title={t('translation:shipmentAdd.productDetails.createNewProduct')}
                            placement="bottomLeft"
                            overlayClassName="mw-none rate-shop-cost-tool-tip"
                          >
                            <SvgConverter Icon={ProductAddIcon} color="header" />
                          </Tooltip>
                        </IconButton>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col>
              <SaveCancelButtons
                editMode={editMode}
                lightMode={false}
                // shipmentEditStatus={shipmentEditStatus}
                setEditMode={(val) => setEditMode(index, val)}
                handleRemove={handleDelete(index)}
                isNew={itemDetails.isNew}
                idSuffix={`product-details-${index + 1}`}
                isModified={null}
                onCancel={handleCancel(index, itemDetails.isNew)}
              />
            </Col>
          </Row>
        </div>
        <div className="px-15 pt-10">
          <Row gutter={15}>
            <Col sm={8}>
              <FormTextArea
                label="Item Description"
                placeholder="Enter Item Description"
                editMode={editMode}
                {...(editMode ? { rows: 3 } : {})}
                name="itemDesc"
                margin="mb-15"
                tabIndex={(index + 1) * 100 + 2}
                maxLength={25}
              />
              <FormInput
                label="Quantity"
                placeholder="Enter Quantity"
                editMode={editMode}
                name="itemQuantity"
                margin="mb-15"
                tabIndex={(index + 1) * 100 + 3}
                inputType="number"
                rules={[
                  {
                    required: true,
                    message: 'Please enter item Quantity',
                  },
                ]}
              />
              <ProductUnitUomSelector
                label="Unit of Measure"
                placeholder="Enter Unit of Measure"
                editMode={editMode}
                name="itemUnitMeasure"
                tabIndex={(index + 1) * 100 + 5}
                margin="mb-15"
                rules={[
                  {
                    required: true,
                    message: 'Please select Unit of Measure',
                  },
                ]}
                shouldUseNativeSelect
              />
            </Col>
            <Col sm={8}>
              <ClaimsRegularReleaseSelector
                label="Full/Release Value"
                placeholder="Enter Full/Release Value"
                editMode={editMode}
                name="fullRelFlag"
                tabIndex={(index + 1) * 100 + 6}
                margin="mb-15"
                rules={[
                  {
                    required: true,
                    message: 'Please select Full/Release Value',
                  },
                ]}
              />
              <ClaimsDispositionMethodSelector
                label="Disposition Method"
                placeholder="Enter Disposition Method"
                editMode={editMode}
                name="dispositionMethod"
                tabIndex={(index + 1) * 100 + 7}
                margin="mb-15"
                rules={[
                  {
                    required: true,
                    message: 'Please select Disposition Method',
                  },
                ]}
              />
              <FormInput
                label="Filed Value"
                placeholder="Enter Filed Value"
                editMode={editMode}
                name="value"
                margin="mb-18"
                tabIndex={(index + 1) * 100 + 8}
                inputType="number"
                rules={[
                  {
                    required: true,
                    message: 'Please enter Filed Value',
                  },
                ]}
              />
            </Col>
            <Col sm={8}>
              <ClaimsPercentageValueSelector
                label="Percentage/Value"
                placeholder="Enter Percentage/Value"
                editMode={editMode}
                name="smType"
                tabIndex={(index + 1) * 100 + 9}
                margin="mb-15"
              />
              <ClaimsMitigatedTypeSelector
                label="Mitigated Type"
                placeholder="Enter Mitigated Type"
                editMode={editMode}
                name="smFlag"
                tabIndex={(index + 1) * 100 + 10}
                margin="mb-15"
              />
              <FormInput
                label="Mitigated Amount"
                placeholder="Enter Mitigated Amount"
                editMode={editMode}
                name="smAmount"
                margin="mb-15"
                tabIndex={(index + 1) * 100 + 11}
                inputType="number"
              />
            </Col>
          </Row>
        </div>
        <HiddenFormInput name="itemSeq" />
        <HiddenFormInput name="oldCommentType" />
        <HiddenFormInput name="isNew" />
        <HiddenFormInput name="editMode" />
        <HiddenFormInput name="claimKey" />
        <HiddenFormInput name="dirtyFlag" />
        <HiddenFormInput name="claimantValue" />
        <HiddenFormInput name="itemUnitWeight" />
      </Form>
    </Paper>
  );
};

export default PreClaimsItemCard;
